import React, { useState } from "react";
import axios from "axios";
import {
  AlertTriangle, Shield, Clock, User, CheckCircle,
  Plus, ChevronDown, X, FileText
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  CRITICAL: "border-red-300 bg-red-50 text-red-800",
  HIGH: "border-amber-300 bg-amber-50 text-amber-800",
  MEDIUM: "border-blue-200 bg-blue-50 text-blue-800",
  LOW: "border-[#e8e0d0] bg-[#faf7f2] text-[#555]",
};

const STATUS_STYLES = {
  open: "bg-red-100 text-red-700",
  in_progress: "bg-amber-100 text-amber-700",
  resolved: "bg-emerald-100 text-emerald-700",
  dismissed: "bg-gray-100 text-gray-600",
};

const EXC_TYPES = [
  { value: "missing_docs", label: "Missing Documents" },
  { value: "stale_quote", label: "Stale Quote" },
  { value: "title_issue", label: "Title Issue" },
  { value: "failed_payment", label: "Failed Payment" },
  { value: "insurance_lapse", label: "Insurance Lapse" },
  { value: "accreditation_gap", label: "Accreditation Gap" },
  { value: "compliance_hold", label: "Compliance Hold" },
  { value: "funding_mismatch", label: "Funding Mismatch" },
  { value: "delinquency", label: "Delinquency" },
  { value: "general", label: "General" },
];

export default function Deal360Exceptions({ exceptions = [], leadId, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ exc_type: "general", severity: "MEDIUM", title: "", detail: "" });
  const [creating, setCreating] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const openExceptions = exceptions.filter(e => e.status === "open" || e.status === "in_progress");
  const resolvedExceptions = exceptions.filter(e => e.status === "resolved" || e.status === "dismissed");

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setCreating(true);
    try {
      await axios.post(`${API}/entity/deal/${leadId}/exceptions`, form, { headers });
      toast.success("Exception created");
      setShowCreate(false);
      setForm({ exc_type: "general", severity: "MEDIUM", title: "", detail: "" });
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create exception");
    } finally {
      setCreating(false);
    }
  };

  const handleResolve = async (excId) => {
    try {
      await axios.post(`${API}/admin/exceptions/resolve/${excId}`, {
        notes: "Resolved from Deal360",
        action: "resolved"
      }, { headers });
      toast.success("Exception resolved");
      onRefresh?.();
    } catch (err) {
      toast.error("Failed to resolve");
    }
  };

  return (
    <div className="space-y-4" data-testid="exceptions-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> Exceptions
          {openExceptions.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-600">{openExceptions.length} open</span>
          )}
        </h3>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
          data-testid="create-exception-btn"
        >
          {showCreate ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {showCreate ? "Cancel" : "New Exception"}
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="rounded-xl border border-red-200 bg-red-50/30 p-4 space-y-3" data-testid="create-exception-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Type</label>
              <select value={form.exc_type} onChange={e => setForm(f => ({ ...f, exc_type: e.target.value }))}
                className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-red-400 focus:outline-none" data-testid="exc-type-select">
                {EXC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-red-400 focus:outline-none" data-testid="exc-severity-select">
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Title</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Brief exception description" className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-red-400 focus:outline-none" data-testid="exc-title-input" />
          </div>
          <div>
            <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Detail</label>
            <textarea value={form.detail} onChange={e => setForm(f => ({ ...f, detail: e.target.value }))}
              placeholder="Additional context..." rows={2} className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-red-400 focus:outline-none resize-none" data-testid="exc-detail-input" />
          </div>
          <button onClick={handleCreate} disabled={creating}
            className="px-4 py-1.5 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 transition-colors" data-testid="submit-exception-btn">
            {creating ? "Creating..." : "Create Exception"}
          </button>
        </div>
      )}

      {/* Open Exceptions */}
      {openExceptions.length === 0 && !showCreate && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center text-xs text-emerald-700 flex items-center justify-center gap-2" data-testid="no-exceptions">
          <CheckCircle className="h-4 w-4" /> No open exceptions
        </div>
      )}

      {openExceptions.map(exc => (
        <div key={exc.exception_id} className={`rounded-xl border p-4 ${SEVERITY_STYLES[exc.severity] || SEVERITY_STYLES.MEDIUM}`} data-testid={`exception-${exc.exception_id}`}>
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <div>
                <div className="text-xs font-bold">{exc.title || exc.exc_type?.replace(/_/g, " ")}</div>
                <div className="text-[10px] opacity-70 mt-0.5">{exc.exc_type?.replace(/_/g, " ")} | {exc.severity}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${STATUS_STYLES[exc.status]}`}>{exc.status}</span>
              <button onClick={() => handleResolve(exc.exception_id)}
                className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors" data-testid={`resolve-exc-${exc.exception_id}`}>
                Resolve
              </button>
            </div>
          </div>
          {exc.detail && <p className="text-[10px] opacity-80 mb-2">{exc.detail}</p>}
          <div className="flex items-center gap-3 text-[9px] opacity-60">
            {exc.created_at && <span><Clock className="h-2.5 w-2.5 inline mr-0.5" />{new Date(exc.created_at).toLocaleDateString()}</span>}
            {exc.owner && <span><User className="h-2.5 w-2.5 inline mr-0.5" />{exc.owner}</span>}
          </div>
        </div>
      ))}

      {/* Resolved */}
      {resolvedExceptions.length > 0 && (
        <details className="group">
          <summary className="text-[10px] text-[#999] cursor-pointer hover:text-[#555] flex items-center gap-1 mb-2">
            <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" /> {resolvedExceptions.length} resolved exception{resolvedExceptions.length !== 1 ? "s" : ""}
          </summary>
          <div className="space-y-2 pl-2">
            {resolvedExceptions.map(exc => (
              <div key={exc.exception_id} className="rounded-lg border border-[#e8e0d0] bg-[#faf7f2] p-3 opacity-70">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-[#555]">{exc.title || exc.exc_type?.replace(/_/g, " ")}</span>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${STATUS_STYLES[exc.status]}`}>{exc.status}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
